/**
 * @file Adattatore CSS per Shadow DOM
 * @description Trasforma il CSS compilato di Tailwind v4 per renderlo compatibile con Shadow DOM.
 *
 * ## Problema
 * Il CSS generato da Tailwind v4 è progettato per essere utilizzato nel documento principale,
 * ma quando viene iniettato in uno Shadow DOM presenta diversi problemi di compatibilità:
 *
 * 1. **Selettore :root**: Nello Shadow DOM, `:root` si riferisce al document root (fuori dallo shadow),
 *    non alla radice dello shadow stesso. Le variabili CSS definite su `:root` non sono accessibili.
 *
 * 2. **Selettori universali**: I selettori come `*`, `::before`, `::after`, `::backdrop` vengono
 *    applicati globalmente al documento, non solo agli elementi nello Shadow DOM.
 *
 * 3. **Direttive @property non supportate**: Le direttive `@property` usate da Tailwind v4 per
 *    registrare custom properties CSS non funzionano all'interno degli Shadow DOM in nessun browser.
 *    Come fallback, Tailwind genera un blocco `@supports` con definizioni manuali delle variabili,
 *    ma questo wrapper deve essere rimosso per garantire che le variabili vengano sempre applicate.
 *
 * ## Soluzione
 * Questo modulo esporta una funzione che applica una serie di trasformazioni regex al CSS
 * per renderlo compatibile con Shadow DOM, mantenendo tutte le funzionalità di Tailwind.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Web_components/Using_shadow_DOM
 * @see https://tailwindcss.com/docs
 */

/**
 * Adatta il CSS per l'uso all'interno di uno Shadow DOM.
 *
 * Applica le seguenti trasformazioni:
 * 1. Sostituisce `:root` con `:host` per le variabili CSS
 * 2. Prefixa i selettori universali con `:host` per scope corretto
 * 3. Rimuove il wrapper `@supports` per garantire l'applicazione delle variabili CSS di Tailwind
 *
 * @param css - Il CSS originale da adattare
 * @returns Il CSS trasformato e compatibile con Shadow DOM
 *
 * @example
 * ```ts
 * const originalCss = await fetch('styles.css').then(r => r.text());
 * const shadowCompatibleCss = adaptCssForShadowDOM(originalCss);
 * shadowRoot.innerHTML = `<style>${shadowCompatibleCss}</style>`;
 * ```
 */
export function adaptCssForShadowDOM(css: string): string {
  let adapted = css;

  // TRASFORMAZIONE 1: :root → :host
  // ─────────────────────────────────────────────────────────────────────────
  // Problema: `:root` nel CSS si riferisce all'elemento radice del documento (html),
  //           che è esterno allo Shadow DOM. Le variabili CSS definite su `:root`
  //           non sono accessibili agli elementi dentro lo shadow.
  //
  // Soluzione: Sostituiamo `:root` con `:host`, che nello Shadow DOM rappresenta
  //            l'elemento ospite (shadow host), rendendo le variabili accessibili.
  //
  // Esempio:
  //   Prima:  :root { --color-primary: #000; }
  //   Dopo:   :host { --color-primary: #000; }
  adapted = adapted.replace(/:root\b/g, ":host");

  // TRASFORMAZIONE 2: Selettori universali → :host scoped
  // ─────────────────────────────────────────────────────────────────────────
  // Problema: Tailwind v4 definisce variabili CSS di utilità (come --tw-border-style)
  //           su selettori universali `*, ::before, ::after, ::backdrop` che applicano
  //           le variabili globalmente a tutto il documento, ma non funzionano correttamente
  //           nello Shadow DOM perché non sono scopate al shadow boundary.
  //
  // Soluzione: Prefissiamo questi selettori con `:host` per creare una cascata corretta:
  //            - `:host` applica le variabili allo shadow host stesso
  //            - `:host *` applica a tutti gli elementi dentro lo shadow
  //            - `:host ::before`, `:host ::after`, `:host ::backdrop` per pseudo-elementi
  //
  // Esempio:
  //   Prima:  *, ::before, ::after, ::backdrop { --tw-border-style: solid; }
  //   Dopo:   :host, :host *, :host ::before, :host ::after, :host ::backdrop { --tw-border-style: solid; }
  adapted = adapted.replace(
    /(\n|^|\s)(\*\s*,\s*::before\s*,\s*::after\s*,\s*::backdrop)(\s*\{)/g,
    "$1:host, :host *, :host ::before, :host ::after, :host ::backdrop$3",
  );

  // TRASFORMAZIONE 3: Rimozione wrapper @supports per compatibilità Shadow DOM
  // ─────────────────────────────────────────────────────────────────────────
  // Problema: Tailwind v4 usa direttive @property per registrare custom properties CSS,
  //           ma queste NON funzionano all'interno degli Shadow DOM in nessun browser.
  //           Come fallback, Tailwind genera un @supports con questa feature query:
  //           @supports ((-webkit-hyphens: none) and ...) or ((-moz-orient: inline) and ...) { ... }
  //
  //           Il contenuto del @supports definisce manualmente tutte le variabili CSS di utilità
  //           (--tw-border-spacing-x, --tw-translate-x, --tw-shadow, ecc.) che sono critiche
  //           per il funzionamento di Tailwind.
  //
  //           Poiché le @property non funzionano negli Shadow DOM, il codice dentro @supports
  //           DEVE sempre essere eseguito, indipendentemente dalla feature query.
  //
  // Soluzione: Rimuoviamo completamente il wrapper @supports e la sua parentesi di chiusura,
  //            mantenendo solo il contenuto interno. Questo garantisce che le variabili CSS
  //            vengano sempre applicate negli Shadow DOM.
  //
  // Esempio:
  //   Prima:  @layer properties { @supports (...) { *, ::before { --tw-shadow: 0 0 #0000; } } }
  //   Dopo:   @layer properties { *, ::before { --tw-shadow: 0 0 #0000; } }
  //
  // Note: Questa trasformazione cerca il pattern specifico di @supports generato da Tailwind v4
  //       e rimuove solo il wrapper @supports (apertura + chiusura), preservando il contenuto
  //       delle regole CSS interne e mantenendo intatta la struttura del @layer circostante.
  adapted = adapted.replace(/@supports\s+\(\(-webkit-hyphens:\s*none\)\s+and[^{]+\)\s*\{([\s\S]*?)\s*\}/g, "$1");

  return adapted;
}
